import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MentionTextarea } from 'src/Components/MentionTextarea';
import { AttachmentsViewer } from 'src/Components/Shared/AttachmentsViewer';
import { useDeleteCommunityFeedItem } from 'src/Hooks/Mutations/useDeleteCommunityFeedItem';
import { useEditCommunityFeedItem } from 'src/Hooks/Mutations/useEditCommunityFeedItem';

import styles from './CommunityFeedItem.module.scss';
import { Props } from './CommunityFeedItem.props';
import { CommunityFeedItemSocialInteractions } from './CommunityFeedItemSocialInteractions';
import { CommunityFeedItemTopRow } from './CommunityFeedItemTopRow';
import { useCommunityFeedItemFormatContent } from './useCommunityFeedItemFormatContent';

const CommunityFeedItem = ({
  feedItem,
  grow,
  areCommentsOpenByDefault,
  onBeginEdit,
  highlightedCommentId,
}: Props) => {
  const { t } = useTranslation();
  const [state, setState] = useState<'view' | 'edit'>('view');

  const { jsx: contentJSX, text: contentText } = useCommunityFeedItemFormatContent(feedItem);

  const { mutate: editFeedItemMutation, isLoading } = useEditCommunityFeedItem({
    onSuccess() {
      setState('view');

      notifications.show({
        message: t('community.editPost.success', 'Votre publication a été modifié.'),
      });
    },
  });

  const { mutate: deleteFeedItemMutation } = useDeleteCommunityFeedItem({
    onSuccess() {
      notifications.show({
        message: t('community.deletePost.success', 'Votre publication a été supprimé.'),
      });
    },
  });

  const [editedText, setEditedText] = useState(contentText);
  const onEdit = () => {
    editFeedItemMutation({ message: editedText, files: null, feedItemId: feedItem.id });
  };

  const viewJSX = (
    <>
      <span className={styles.paragraph}>{contentJSX}</span>

      {feedItem.attachments && feedItem.attachments.length > 0 && (
        <div className={styles.attachments}>
          <AttachmentsViewer attachments={feedItem.attachments} />
        </div>
      )}

      <CommunityFeedItemSocialInteractions
        feedItem={feedItem}
        areCommentsOpenByDefault={areCommentsOpenByDefault}
        grow={grow}
        highlightedCommentId={highlightedCommentId}
        excerpt={contentText}
      />
    </>
  );

  const editJSX = (
    <>
      <MentionTextarea
        value={editedText}
        onChange={setEditedText}
        placeholder={t('community.editPost.placeholder', 'Modifiez votre publication.')}
      />

      <div className={styles.separator} />

      <div className={styles.publishContainer}>
        <Button
          onClick={() => setState('view')}
          className={styles.commentButton}
          disabled={isLoading}
        >
          {t('common.cancel', 'Annuler')}
        </Button>

        <Button onClick={onEdit} className={styles.commentButton} loading={isLoading}>
          {t('common.publish', 'Publier')}
        </Button>
      </div>
    </>
  );

  return (
    <article className={styles.container} style={{ flexGrow: grow ? 1 : undefined }}>
      <CommunityFeedItemTopRow
        feedItem={feedItem}
        onEdit={() => (onBeginEdit ? onBeginEdit() : setState('edit'))}
        onDelete={() => deleteFeedItemMutation({ feedItemId: feedItem.id })}
      />

      {state === 'view' && viewJSX}

      {state === 'edit' && editJSX}
    </article>
  );
};

export default CommunityFeedItem;
